import apiClient from "services/ApiClient";
import type { ResponseModel } from "services/ApiClient/responseModel";
import {
  PlatformEvent,
  PlatformEventExtended,
  CreatePlatformEventPayload,
} from "./types";

const BASE_PATH = "/api/platform-events";

/**
 * Fetch platform events with query string (for use with GenericTable)
 *
 * The query string is built by GenericTable's useQueryString hook and includes
 * pagination, sorting, filters, and additional queries.
 *
 * @param query - Query string starting with "?" containing URL-encoded parameters
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to ResponseModel containing events array and pagination metadata
 *
 * @example
 * ```typescript
 * // Query string format (built automatically by GenericTable):
 * // ?pagination[page]=1&pagination[per_page]=50&sort_by=occurred_at&sort_direction=desc&event_type=email
 *
 * // Supported query parameters:
 * //
 * // Filtering:
 * //   - event_type: Filter by event type prefix (e.g., "email" matches "email.sent", "email.failed")
 * //   - event_name: Filter by exact event name (e.g., "email.sent")
 * //   - new_hire_journey_id: Filter by new hire journey ID
 * //   - new_hire_id: Filter by new hire user ID
 * //   - actor_id: Filter by actor (user who triggered the event)
 * //   - entity_type: Filter by entity type (e.g., "Task", "Email", "Meeting", "Message", "NewHireJourney", "Issue", "FormResponse")
 * //   - start_date: Filter events on or after this date (ISO 8601 format: YYYY-MM-DD)
 * //   - end_date: Filter events on or before this date (ISO 8601 format: YYYY-MM-DD)
 * //
 * // Pagination (standard format):
 * //   - pagination[page]: Page number (default: 1)
 * //   - pagination[per_page]: Items per page (default: 50, max: 200)
 * //
 * // Sorting (standard format):
 * //   - sort_by: Field to sort by (e.g., "occurred_at", "event_name", "created_at")
 * //   - sort_direction: Sort direction ("asc" or "desc")
 * ```
 */
export async function fetchPlatformEvents(
  query: string,
  signal?: AbortSignal,
): Promise<ResponseModel<PlatformEvent[]>> {
  return apiClient
    .performRequest<ResponseModel<PlatformEvent[]>>(`${BASE_PATH}${query}`, undefined, signal)
    .then((response) => response.payload);
}

/**
 * Get a single platform event by ID
 */
export async function getPlatformEvent(id: string): Promise<{ data: PlatformEventExtended }> {
  return apiClient.get<{ data: PlatformEventExtended }>(`${BASE_PATH}/${id}`);
}

/**
 * Create a new platform event (for frontend tracking)
 */
export async function createPlatformEvent(
  payload: CreatePlatformEventPayload,
): Promise<{ data: PlatformEvent }> {
  return apiClient.post<{ data: PlatformEvent }>(BASE_PATH, payload);
}
